import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// Per-run receipt colors, keyed by HighlighterRunStatus.
const RUN_COLORS = {
  running: { bg: "#DBE7FF", fg: "#1A3B7F" },
  completed: { bg: "#D6F5E2", fg: "#0F5E2E" },
  empty: { bg: "#EBEBEB", fg: "#5A5A5A" },
  failed: { bg: "#FFD6D6", fg: "#7A1A1A" },
};

function fmtDateIL(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleDateString("en-GB", {
    timeZone: "Asia/Jerusalem",
    day: "2-digit",
    month: "2-digit",
  });
}

function fmtTimeIL(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString("en-GB", {
    timeZone: "Asia/Jerusalem",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function RunRow({ run, text }) {
  const status = run.status || "running";
  const color = RUN_COLORS[status] || RUN_COLORS.running;
  const shows = (run.shows || []).filter(Boolean);
  const window =
    run.record_start_utc_ms && run.record_end_utc_ms
      ? `${fmtTimeIL(run.record_start_utc_ms)}–${fmtTimeIL(run.record_end_utc_ms)}`
      : "";
  const counts =
    run.clips_uploaded != null || run.clips_found != null
      ? `${run.clips_uploaded ?? 0}/${run.clips_found ?? 0} ${text.runs.clips}`
      : "";

  return (
    <Box sx={{ py: 1.25 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Chip
          dir="ltr"
          size="small"
          label={text.runs.status[status] || status}
          sx={{ bgcolor: color.bg, color: color.fg, fontWeight: 600 }}
        />
        {counts ? (
          <Typography variant="caption" dir="ltr" sx={{ unicodeBidi: "isolate" }}>
            {counts}
          </Typography>
        ) : null}
        <Typography
          variant="caption"
          dir="ltr"
          sx={{ ml: "auto", color: "text.secondary", unicodeBidi: "isolate" }}
        >
          {fmtDateIL(run.started_at || run.record_start_utc_ms)}
          {window ? ` · ${window}` : ""}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {shows.length ? shows.join(" · ") : text.runs.noShow}
      </Typography>
      {run.error ? (
        <Typography variant="caption" sx={{ color: "error.main" }}>
          {run.error}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function RunsDrawer({ open, onClose, runs, loading, text, rtl }) {
  return (
    <Drawer
      anchor={rtl ? "left" : "right"}
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 380 } } }}
    >
      <Box dir={rtl ? "rtl" : "ltr"} sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {text.runs.title}
          </Typography>
          <Tooltip title={text.runs.tooltip} arrow>
            <IconButton size="small" onClick={onClose}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : !runs.length ? (
          <Typography variant="body2" sx={{ py: 4, color: "text.secondary" }}>
            {text.runs.empty}
          </Typography>
        ) : (
          runs.map((run, i) => (
            <Box key={run.run_id}>
              {i > 0 ? <Divider /> : null}
              <RunRow run={run} text={text} />
            </Box>
          ))
        )}
      </Box>
    </Drawer>
  );
}
